import { Clipboard, Link as LinkIcon, Trash, UploadIcon } from 'lucide-react'
import React, { useRef } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Image } from 'src/shared/components/ui'
import { Button } from 'src/shared/components/ui/button'
import { ScrollArea } from 'src/shared/components/ui/scroll-area'
import { Textarea } from 'src/shared/components/ui/textarea'
import { parseImageUrls, removeImageUrl } from '../../utils'
import { SCROLL_HEIGHTS } from '../../consts'

interface ImageUploadSectionProps {
  imageUrls: string
  setImageUrls: (urls: string) => void
  handleImageUpload: (file: File) => Promise<void>
  isUploading: boolean
  error: string | null
  fileInputId: string
  label?: string
  placeholder?: string
  scrollHeight?: string
  showPreview?: boolean
  imageSize?: { width: number; height: number }
  hideUploadButton?: boolean
}

export const ImageUploadSection = ({
  imageUrls,
  setImageUrls,
  handleImageUpload,
  isUploading,
  error,
  fileInputId,
  label = 'Image URLs',
  placeholder = ' ',
  scrollHeight = SCROLL_HEIGHTS.NEW_COLOR_IMAGES,
  showPreview = true,
  imageSize,
  hideUploadButton = false,
}: ImageUploadSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageUrlList = parseImageUrls(imageUrls)
  const hasImages = imageUrlList.length > 0

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await handleImageUpload(file)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileInputClick = () => {
    document.getElementById(fileInputId)?.click()
  }

  const handleRemoveImage = (url: string) => {
    setImageUrls(removeImageUrl(imageUrls, url))
  }

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items
    if (!items) return

    // Check if clipboard contains image data
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault() // Prevent default paste behavior

        let file = item.getAsFile()
        if (!file) return

        // Ensure file has a proper name with extension for upload
        // Clipboard images might not have a filename, so we derive it from MIME type
        if (!file.name || !file.name.includes('.')) {
          const mimeType = item.type
          const extension = mimeType.split('/')[1] || 'png' // Default to png if unknown
          const fileName = `pasted-image-${Date.now()}.${extension}`
          file = new File([file], fileName, { type: mimeType })
        }

        // Upload the image - handleImageUpload will automatically add the URL via callback
        await handleImageUpload(file)
        return
      }
    }
  }

  return (
    <Column className="gap-2">
      <Column className="gap-1.5">
        <Textarea
          label={label}
          rows={2}
          value={imageUrls}
          onChange={(e) => setImageUrls(e.target.value)}
          onPaste={handlePaste}
          placeholder={
            placeholder ||
            'Paste image URLs (comma-separated) or paste an image directly'
          }
        />
        <div className="flex flex-wrap items-center gap-3 px-2">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Clipboard className="h-3 w-3" />
            <span>Paste image (Ctrl+V / Cmd+V)</span>
          </span>
          {!hideUploadButton && (
            <span className="text-xs text-muted-foreground flex items-center gap-1.5">
              <UploadIcon className="h-3 w-3" />
              <span>Upload image</span>
            </span>
          )}
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            <LinkIcon className="h-3 w-3" />
            <span>Paste image URLs</span>
          </span>
        </div>
      </Column>

      {!hideUploadButton && (
        <Row className="gap-2 mt-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            id={fileInputId}
            onChange={handleFileChange}
          />

          <Button
            type="button"
            variant="secondary"
            onClick={handleFileInputClick}
            disabled={isUploading}
          >
            <UploadIcon className="h-4 w-4 mr-2" />
            {isUploading ? 'Uploading...' : 'Upload image'}
          </Button>
        </Row>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {showPreview && hasImages && (
        <ImagePreview
          imageUrlList={imageUrlList}
          onRemoveImage={handleRemoveImage}
          scrollHeight={scrollHeight}
          imageSize={imageSize}
        />
      )}
    </Column>
  )
}

interface ImagePreviewProps {
  imageUrlList: string[]
  onRemoveImage: (url: string) => void
  scrollHeight?: string
  imageSize?: { width: number; height: number }
}

const ImagePreview = ({
  imageUrlList,
  onRemoveImage,
  scrollHeight = SCROLL_HEIGHTS.NEW_COLOR_IMAGES,
  imageSize = { width: 120, height: 120 },
}: ImagePreviewProps) => {
  return (
    <Column className="gap-2">
      <p className="text-sm font-medium text-muted-foreground">
        Preview images ({imageUrlList.length})
      </p>

      <ScrollArea className={scrollHeight}>
        <Row className="gap-3 flex-wrap p-1">
          {imageUrlList.map((url: string) => (
            <div className="relative group" key={url}>
              <Image
                src={url}
                alt="Preview"
                width={imageSize.width}
                height={imageSize.height}
                className="rounded-lg border-2 border-border object-cover shadow-sm"
              />
              <Button
                size="icon"
                className="absolute -top-2 -right-2 h-6 w-6 bg-destructive hover:bg-destructive/90 text-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                variant="destructive"
                onClick={() => onRemoveImage(url)}
              >
                <Trash className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </Row>
      </ScrollArea>
    </Column>
  )
}
